import { computed, effect, signal, Service } from '@angular/core';
import { ThemeMode } from '@interfaces/theme.interface';

const KEY = 'ptp-theme';

/**
 * Owns the app theme — a plain light/dark choice stamped on `<html
 * data-theme>` and persisted in localStorage. First visit (no stored
 * value) seeds from the OS `prefers-color-scheme` once; after that it's
 * whatever the user last picked. A matching pre-paint script in
 * index.html applies it before Angular boots (no flash). The toggle
 * suppresses transitions for one frame so the flip is a hard cut.
 */
@Service()
export class ThemeService {
  readonly mode = signal<ThemeMode>(this.initial());

  /** the mode the toggle would switch to */
  readonly other = computed<ThemeMode>(() => (this.mode() === 'dark' ? 'light' : 'dark'));
  readonly switchLabel = computed(() => `Switch to ${this.other()} theme`);

  constructor() {
    effect(() => {
      const m = this.mode();
      document.documentElement.setAttribute('data-theme', m);
      try {
        localStorage.setItem(KEY, m);
      } catch {
        /* private mode — the in-memory signal still drives the UI */
      }
    });
  }

  toggle(): void {
    const root = document.documentElement;
    root.classList.add('theme-switching');
    this.mode.update((m) => (m === 'dark' ? 'light' : 'dark'));
    requestAnimationFrame(() => root.classList.remove('theme-switching'));
  }

  private initial(): ThemeMode {
    try {
      const v = localStorage.getItem(KEY);
      if (v === 'light' || v === 'dark') {
        return v;
      }
    } catch {
      /* ignore */
    }
    return typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
}
